import * as Sentry from '@sentry/react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { OutboundTaskTypeKey, OutboundTaskType, TaskComment, TaskEvent } from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow from 'design-system/src/components/ButtonRow/ButtonRow';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import DeclineModal from '../../DeclineModal';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { FormActionContainer } from '../../actions/shared/styles';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { EmbeddedForm } from '../../../EmbeddedForm';
import { validateEmbeddedFormInfo } from '../../../../lib/embeddedFormInfo';
import { tasksQueryKey } from '../../../../queries';
import { StructuredTaskViewWrapper } from '..';

/**
 * Dedicated task view for Form Signature (Form completion request) tasks with an embedded form.
 */
const FormSignatureTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showNewFollowupTaskModal, setShowNewFollowupTaskModal] = useState(false);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;

  useEffect(() => {
    const handleFormMessage = (messageEvent: MessageEvent) => {
      if (!messageEvent.data) return;

      try {
        if (!messageEvent.data.type) return;

        const event = messageEvent.data.type.split('embed.form.')[1];

        switch (event) {
          case 'completed':
            onTaskUpdate();
            toast.custom(() => <Toast variant="success">Form signed.</Toast>);
            navigate('/tasks');
            queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
            break;
          case 'exception':
            toast.custom(() => <Toast variant="error">Error, please try again.</Toast>);
            break;
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    };

    window.addEventListener('message', handleFormMessage);
    return () => window.removeEventListener('message', handleFormMessage);
  }, [navigate, onTaskUpdate, queryClient]);

  const isCompleted = task.status === 'completed';
  const initialComment = task.timeline?.[0] as TaskComment | undefined;
  const secondTimelineItem = isCompleted && task.timeline && task.timeline.length > 1 ? task.timeline[1] : undefined;
  const completionEvent = secondTimelineItem?.item_type === 'event' ? (secondTimelineItem as TaskEvent) : undefined;
  const formInfo = task.form ? validateEmbeddedFormInfo(task) : null;

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: { data: drug },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {/* Completion message for completed tasks */}
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        {/* Instructions */}
        <div className={`mb-6 flex flex-col gap-4 ${isCompleted ? 'border-b-grayscale-100 pb-4 border-b-solid' : ''}`}>
          <TaskInstructionSummary>
            Please have the prescriber complete and sign the enrollment form below:
          </TaskInstructionSummary>
          {initialComment && initialComment.item_type === 'comment' && (
            <Typography styledAs="bodyExtraSmallDMSans">
              <RenderHtmlString>{initialComment.message}</RenderHtmlString>
            </Typography>
          )}
        </div>

        {!isCompleted && formInfo && (
          <FormActionContainer>
            <EmbeddedForm
              formInfo={formInfo}
              {...(formInfo.form_source === 'form_filler' && {
                onComplete: async () => {
                  await onTaskUpdate();
                  toast.custom(() => <Toast variant="success">Form signed.</Toast>);
                  navigate('/tasks');
                  queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
                },
              })}
            />
            <ButtonRow className="!justify-start">
              <Button variant="tertiary" size="sm" onClick={() => setShowDeclineModal(true)}>
                Decline enrollment
              </Button>
              <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
            </ButtonRow>
          </FormActionContainer>
        )}
      </StructuredTaskViewWrapper>

      {showDeclineModal && (
        <DeclineModal
          closeModal={() => setShowDeclineModal(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key as OutboundTaskTypeKey}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showNewFollowupTaskModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowNewFollowupTaskModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default FormSignatureTaskView;
